<script setup lang="ts">
import type { IProduct } from '~/server/utils/products'
import * as Sentry from '@sentry/nuxt'
import { useNow } from '@vueuse/core'
import { BadgeCheck, CircleSmall, SquarePen } from 'lucide-vue-next'

interface Products {
  amount: number
  tariffs: IProduct[]
}
const currency = useCurrency()
const products = useState<Products[]>('products')

await callOnce(async () => {
  try {
    products.value = await $fetch<Products[]>(`/api/products`)
  }
  catch (error) {
    console.error('Error loading products:', error)

    // Отправляем ошибку в Sentry с контекстом
    Sentry.withScope((scope) => {
      scope.setTag('component', 'Calculator')
      scope.setTag('action', 'load_products')
      scope.setLevel('error') // Критическая ошибка - калькулятор не работает без продуктов
      Sentry.captureException(error)
    })

    // Устанавливаем пустой массив как fallback
    products.value = []
  }
})

const values = reactive({
  amount: 33000,
  term: 25,
})
const mainStore = useMainStore()
watch(values, () => {
  mainStore.lead_limit = values.amount
  mainStore.lead_term = values.term
}, {
  immediate: true,
})
// Функция для поиска ближайшего значения в массиве
function findClosest(array: number[], target: number) {
  return array.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
  )
}

// Обработка данных продуктов
const productData = computed(() => {
  if (!products.value)
    return null

  const amounts = [...new Set(products.value.map(p => p.amount))].sort((a, b) => a - b)
  const terms = [...new Set(
    products.value.flatMap(p =>
      p.tariffs.map(t => Math.floor(t.loan_term / 7)),
    ),
  )].sort((a, b) => a - b)

  return { amounts, terms }
})

// Валидация и нормализация значений
const normalizedValues = computed(() => {
  if (!productData.value)
    return { amount: 0, term: 0 }

  const amount = values.amount

  const term = values.term

  return { amount, term }
})

// Текущий выбранный продукт и тариф
const currentProduct = computed(() => {
  return products.value?.find(p => p.amount === normalizedValues.value.amount)
})

const currentTariff = computed(() => {
  if (!currentProduct.value)
    return null

  // Ищем точное совпадение по сроку
  const exactTariff = currentProduct.value.tariffs.find(t =>
    Math.floor(t.loan_term / 7) === normalizedValues.value.term,
  )

  return exactTariff
})

// Расчеты платежей
const paymentInfo = computed(() => {
  if (!currentTariff.value)
    return null

  const weeklyPayment = Math.ceil(currentTariff.value.pay_amount_ / 100)
  const totalWeeks = Math.floor(currentTariff.value.loan_term / 7)
  const paymentDate = new Date(Date.now() + 1000 * 60 * 60 * 24 * 7 * totalWeeks)

  const totalAmount = weeklyPayment * totalWeeks
  const interest = totalAmount - normalizedValues.value.amount
  const dailyInterest = (interest / normalizedValues.value.amount) / (totalWeeks * 7) * 100

  return {
    weekly: weeklyPayment,
    date: paymentDate.toLocaleDateString('ru-RU'),
    totalWeeks,
    dailyInterest: Number(dailyInterest.toFixed(2)),
  }
})

const isEditing = ref(false)
const inputRef = useTemplateRef('inputRef')
const paymentInfoRef = useTemplateRef<Element>('paymentInfoRef')

// Функция для активации редактирования
function enableEditing() {
  isEditing.value = true
  nextTick(() => inputRef.value?.focus())
  requestAnimationFrame(() => {
    inputRef.value?.focus()
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      setTimeout(() => inputRef.value?.focus(), 100)
    }
  })
}
function handleInput(event: Event) {
  const target = event.target as HTMLInputElement
  let value = target.value.replace(/\D/g, '') // Удаляем все нецифровые символы
  value = value.slice(0, 6) // Ограничиваем длину до 6 цифр

  // Обновляем значение
  const numericValue = Number.parseInt(value) || 0
  target.value = numericValue.toString()
}
// Обработчик изменения суммы
function handleAmountChange(event: Event) {
  const target = event.target as HTMLInputElement
  let value = Number.parseInt(target.value.replace(/\D/g, '')) || 0
  value = Math.max(Math.round(value / 500) * 500, 3000) // Минимум 3000
  updateAmount(value)
  isEditing.value = false
}

// Обработчики изменений
function updateAmount(newAmount: number | undefined) {
  if (!newAmount)
    return

  newAmount = Math.max(newAmount, 3000)
  newAmount = Math.min(newAmount, 100000)

  values.amount = newAmount

  if (values.amount < 20000) {
    values.term = 12
  }
  if (values.amount > 20000) {
    values.term = 16
  }
  if (values.amount > 30000) {
    values.term = 25
  }
  if (!currentTariff.value && productData.value) {
    const availableTerms = products.value
      ?.filter(p => p.amount === newAmount)
      .flatMap(p => p.tariffs.map(t => Math.floor(t.loan_term / 7))) || []
    if (availableTerms.length > 0) {
      values.term = findClosest(availableTerms, values.term)
    }
  }
}

function updateTerm(newTerm: number | undefined) {
  if (!newTerm)
    return

  values.term = newTerm
  if (values.term === 12 && values.amount > 20000) {
    values.amount = 20000
  }
  if (values.term === 25 && values.amount < 30500) {
    values.amount = 30500
  }
  if (values.term === 16 && values.amount < 20000) {
    values.amount = 20000
  }
  if (!currentTariff.value && productData.value) {
    const availableAmounts = products.value
      ?.filter(p => p.tariffs.some(t =>
        Math.floor(t.loan_term / 7) === newTerm,
      ))
      .map(p => p.amount) || []

    if (availableAmounts.length > 0) {
      values.amount = findClosest(availableAmounts, values.amount)
    }
  }
}

const formattedAmount = computed(() => {
  return `${currency.format(normalizedValues.value.amount)} ₽`
})

const formattedTerm = computed(() => {
  return `${normalizedValues.value.term} недель`
})

const isPopoverOpen = ref(false)
</script>

<template>
  <section class="pt-4 md:pt-8">
    <div class="container mx-auto px-4">
      <div class="bg-white rounded-3xl shadow-xl p-6 md:p-8">
        <!-- Основной блок калькулятора -->
        <div class="grid gap-4 md:gap-6 md:grid-cols-3 items-center">
          <!-- Слайдер суммы -->
          <div class="grid grid-cols-1 md:grid-cols-3 md:col-span-3 gap-4 items-center">
            <!-- Мобильная версия -->
            <div class="flex flex-col gap-2 items-center w-full md:hidden mb-2">
              <label class="text-gray-600">Желаемая сумма</label>
              <div class="bg-primary/10 w-full text-center text-primary text-xl rounded-xl px-4 py-3 font-bold flex items-center justify-center gap-1">
                <template v-if="!isEditing">
                  <span class="min-w-[84px]">{{ formattedAmount }}</span>
                  <SquarePen
                    class="size-5 cursor-pointer transition-colors opacity-50 hover:opacity-80 mt-1"
                    @click="enableEditing"
                  />
                </template>

                <input
                  v-else
                  ref="inputRef"
                  type="number"
                  :value="values.amount"
                  class="w-full bg-transparent text-center outline-none"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  min="3000"
                  @touchstart="(e: any) => e.target.focus()"
                  @blur="handleAmountChange"
                  @keyup.enter="handleAmountChange"
                  @input="handleInput"
                >
              </div>
            </div>

            <!-- Десктопная версия -->
            <div class="hidden md:block md:col-span-2 space-y-2">
              <label class="block text-gray-600 md:text-start text-center">Желаемая сумма</label>
              <CalculatorSumSlider
                :model-value="values.amount"
                :limit="100000"
                @update:model-value="updateAmount"
              />
            </div>

            <div class="hidden md:flex bg-primary/10 text-primary text-xl rounded-xl p-3 font-bold h-12 items-center justify-center gap-1">
              <template v-if="!isEditing">
                <span class="min-w-[84px]">{{ formattedAmount }}</span>
                <SquarePen
                  class="size-5 cursor-pointer transition-colors opacity-50 hover:opacity-80 mt-1"
                  @click="enableEditing"
                />
              </template>

              <input
                v-else
                ref="inputRef"
                type="number"
                min="3000"
                :value="values.amount"
                class="w-32 bg-transparent text-center outline-none"
                inputmode="numeric"
                pattern="[0-9]*"
                @blur="handleAmountChange"
                @keyup.enter="handleAmountChange"
                @input="handleInput"
              >
            </div>

            <!-- Мобильный слайдер -->
            <div class="w-full md:hidden">
              <CalculatorSumSlider
                :model-value="values.amount"
                :limit="100000"
                @update:model-value="updateAmount"
              />
            </div>
          </div>

          <!-- Блок срока -->
          <div class="hidden md:grid grid-cols-1 md:grid-cols-3 md:col-span-3 gap-4 items-center">
            <!-- Мобильная версия -->
            <div class="flex flex-col gap-2 items-center w-full md:hidden mb-2">
              <label class="text-gray-600">Предпочтительный срок</label>
              <div class="bg-primary/10 w-full text-center text-primary text-xl rounded-xl px-4 py-3 font-bold">
                {{ formattedTerm }}
              </div>
            </div>

            <!-- Десктопная версия -->
            <div class="hidden md:block md:col-span-2 space-y-2">
              <label class="block text-gray-600 md:text-start text-center">Предпочтительный срок</label>
              <CalculatorTermSlider
                :model-value="values.term"
                :terms="productData?.terms"
                @update:model-value="updateTerm"
              />
            </div>

            <div class="hidden md:flex bg-primary/10 text-primary text-xl rounded-xl p-3 font-bold h-12 items-center justify-center">
              {{ formattedTerm }}
            </div>

            <!-- Мобильный слайдер -->
            <div class="w-full md:hidden">
              <CalculatorTermSlider
                :model-value="values.term"
                :terms="productData?.terms"
                @update:model-value="updateTerm"
              />
            </div>
          </div>

          <!-- Информация о платежах -->
          <Popover v-model:open="isPopoverOpen" :delay-duration="0">
            <div
              v-if="paymentInfo" id="payment-info"
              ref="paymentInfoRef"
              class="md:col-span-2"
              @mouseenter="isPopoverOpen = true" @mouseleave="isPopoverOpen = false"
            >
              <div class="rounded-xl bg-gray-100 py-2 grid md:gap-2 md:grid-cols-2 text-center md:text-start md:min-h-16 h-full">
                <div class="px-4 flex justify-center  flex-wrap items-center gap-1">
                  <p class="text-gray-600">
                    Возврат до:
                  </p>
                  <p class="text-lg font-semibold min-w-24">
                    {{ paymentInfo.date }}
                  </p>
                </div>
                <div class="px-4 flex justify-center  flex-wrap items-center gap-1">
                  <p class="text-gray-600">
                    {{ values.amount <= 30000 ? 'Платеж раз в 2 недели:' : 'Еженедельный платеж:' }}
                  </p>
                  <p class="text-lg font-semibold min-w-16">
                    {{ currency.format(paymentInfo.weekly) }} ₽
                  </p>
                </div>
              </div>
            </div>
            <PopoverContent :reference="paymentInfoRef!" side="bottom" class="text-lg font-semibold w-full text-gray-600">
              от 0,000 до 292,000% годовых
            </PopoverContent>
          </Popover>

          <GetMoneyButton />
        </div>

        <!-- Преимущества -->
        <div class="mt-8 grid gap-1 md:gap-4 md:grid-cols-3 justify-center w-full">
          <div
            v-for="(benefit, i) in [
              'Одобрение и деньги уже через 10 минут',
              'Без скрытых комиссий и штрафов',
              'Круглосуточно без выходных',
            ]"
            :key="i"
            class="flex flex-col items-center justify-center w-full text-center"
          >
            <div class="flex items-center w-full gap-1 rounded-lg justify-center mb-1">
              <BadgeCheck class="hidden md:block w-5 text-primary shrink-0" />
              <span class="text-gray-600">{{ benefit }}</span>
            </div>

            <CircleSmall v-if="i !== 2" class="md:hidden w-3 shrink-0 fill-gray-300 text-gray-300" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
@reference "assets/css/main.css";

.slider-track {
  @apply h-2 bg-gray-200 rounded-full;
}
input {
  min-width: 100px;
  /* Фикс для iOS */
  transform: translateZ(0);
}
.slider-range {
  @apply bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full;
}

.slider-thumb {
  @apply w-5 h-5 bg-white border-2 border-blue-500 rounded-full shadow-lg;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
